import * as React from 'react'
import { useCallback, useState } from 'react'
import { useFormikContext } from 'formik'
import { ListItem, NotificationFormModel } from './typings'
import { getChildren, getParents } from './constants'
import { CustomizedTreeView } from '@sales-center/components'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import IndeterminateCheckBox from '@mui/icons-material/IndeterminateCheckBox'
import Stack from '@mui/material/Stack'

export const SalesTeamTree: React.FC = () => {
  const {
    values: { list },
    setFieldValue,
  } = useFormikContext<NotificationFormModel>()

  const setList = useCallback(
    (list: ListItem[]) => setFieldValue('list', list),
    [setFieldValue],
  )

  //#region tree state
  const [expanded, setExpanded] = useState<string[]>(
    list.filter((i) => i.parentId === null).map((i) => i.id),
  )

  const onChangeSelected = useCallback(
    (item: ListItem, state?: boolean) => {
      const newList = [...list]
      const itemState = state ?? !item.selected

      const childrenToUpdate = [item, ...getChildren(item, list)]
      childrenToUpdate.map((item) => {
        const index = list.indexOf(item)
        if (index !== -1) {
          newList[index] = {
            ...item,
            selected: itemState,
            indeterminate: false,
          }
        }
      })

      const parentsToUpdate = getParents(item, newList)
      parentsToUpdate.map((item) => {
        const index = list.indexOf(item)
        if (index !== -1) {
          newList[index] = {
            ...item,
            selected: false,
            indeterminate: true,
          }
        }
      })

      setList([...newList])
    },
    [list, setList],
  )
  //#endregion

  //#region button click handlers
  const onExpandAll = useCallback(
    () => setExpanded(list.map((item) => item.id)),
    [list],
  )

  const onCollapseAll = useCallback(() => setExpanded([]), [])

  const onSelectAll = useCallback(
    () =>
      setList(
        list.map((item) => ({
          ...item,
          selected: true,
          indeterminate: false,
        })),
      ),
    [list, setList],
  )

  const onSelectNone = useCallback(
    () =>
      setList(
        list.map((item) => ({
          ...item,
          selected: false,
          indeterminate: false,
        })),
      ),
    [list, setList],
  )
  //#endregion

  return list.length > 0 ? (
    <>
      <Stack direction="row" gap={1}>
        <Button
          color="secondary"
          variant="contained"
          onClick={onExpandAll}
          size="medium"
        >
          Expand All
        </Button>

        <Button
          color="secondary"
          variant="contained"
          onClick={onCollapseAll}
          size="medium"
        >
          Collapse All
        </Button>

        <Button
          color="secondary"
          variant="contained"
          onClick={onSelectAll}
          size="medium"
        >
          Select All
        </Button>

        <Button
          color="secondary"
          variant="contained"
          onClick={onSelectNone}
          size="medium"
        >
          Select None
        </Button>
      </Stack>

      <Box overflow="hidden auto" mt={3}>
        <CustomizedTreeView
          items={list}
          idPropName="id"
          labelPropName="label"
          parentIdPropName="parentId"
          expanded={expanded}
          checkbox={(item) => (
            <Checkbox
              color="primary"
              onClick={(e) => e.stopPropagation()}
              onChange={() => onChangeSelected(item)}
              indeterminate={item.indeterminate}
              indeterminateIcon={<IndeterminateCheckBox color="primary" />}
              checked={item.selected}
              size="small"
            />
          )}
        />
      </Box>
    </>
  ) : null
}
